import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Clock } from 'lucide-react';

const ContactPage = () => {

  const openingHours = [
    { day: 'Monday', hours: '11:30 am – 9 pm' },
    { day: 'Tuesday', hours: '11:30 am – 9 pm' },
    { day: 'Wednesday', hours: '11:30 am – 9 pm' },
    { day: 'Thursday', hours: '11:30 am – 9 pm' },
    { day: 'Friday', hours: '11:30 am – 10 pm' },
    { day: 'Saturday', hours: '11:30 am – 10 pm' },
    { day: 'Sunday', hours: '11:30 am – 4 pm' }
  ];

  // Function to get current status and countdowns
  const getCurrentStatus = () => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour * 60 + currentMinute;
    
    // Define opening and closing times for each day
    let openingTime = 690; // 11:30 AM
    let closingTime = 1260; // 9 PM (Mon-Thu)
    let currentHours = '11:30 AM - 9 PM';
    
    if (dayOfWeek === 5 || dayOfWeek === 6) { // Fri-Sat
      closingTime = 1320; // 10 PM
      currentHours = '11:30 AM - 10 PM';
    } else if (dayOfWeek === 0) { // Sunday
      closingTime = 960; // 4 PM
      currentHours = '11:30 AM - 4 PM';
    }
    
    const isOpen = currentTime >= openingTime && currentTime <= closingTime;
    
    // Calculate countdowns
    let countdown = '';
    let countdownType = '';
    
    if (!isOpen) {
      // Calculate time until opening
      const tomorrow = new Date(now);
      if (currentTime >= closingTime) {
        // We're closed for today, countdown to tomorrow
        tomorrow.setDate(tomorrow.getDate() + 1);
      }
      tomorrow.setHours(11, 30, 0, 0); // Set to 11:30 AM
      
      const timeUntilOpening = tomorrow.getTime() - now.getTime();
      const hours = Math.floor(timeUntilOpening / (1000 * 60 * 60));
      const minutes = Math.floor((timeUntilOpening % (1000 * 60 * 60)) / (1000 * 60));
      
      countdown = `${hours}hr ${minutes}min until opening`;
      countdownType = 'opening';
    } else {
      // Calculate time until closing
      const closingDate = new Date(now);
      closingDate.setHours(Math.floor(closingTime / 60), closingTime % 60, 0, 0);
      
      const timeUntilClosing = closingDate.getTime() - now.getTime();
      const hours = Math.floor(timeUntilClosing / (1000 * 60 * 60));
      const minutes = Math.floor((timeUntilClosing % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours <= 2) {
        // Show closing soon warning
        countdown = `Closing in ${hours}hr ${minutes}min`;
        countdownType = 'closing-soon';
      } else {
        // Show regular closing time
        countdown = `Open for ${hours}hr ${minutes}min`;
        countdownType = 'open';
      }
    }
    
    return { isOpen, currentHours, countdown, countdownType };
  };

  return (
    <div 
      className="min-h-screen bg-background" 
      style={{ 
        fontFamily: "'Segoe UI', sans-serif",
        backgroundColor: "#f8f9fa",
        background: `
          conic-gradient(from -45deg at calc(100%/3) calc(100%/3), rgba(245,245,245,0.25) 90deg, #0000 0),
          conic-gradient(from -135deg at calc(100%/3) calc(2*100%/3), rgba(245,245,245,0.25) 90deg, rgba(233,233,233,0.25) 0 135deg, #0000 0),
          conic-gradient(from 135deg at calc(2*100%/3) calc(2*100%/3), rgba(245,245,245,0.25) 90deg, rgba(233,233,233,0.25) 0 135deg, #0000 0),
          conic-gradient(from 45deg at calc(2*100%/3) calc(100%/3), rgba(245,245,245,0.25) 90deg, rgba(233,233,233,0.25) 0 135deg, #0000 0, rgba(245,245,245,0.25) 0 225deg, rgba(233,233,233,0.25) 0)
        `,
        backgroundSize: "64px 64px"
      }}
    >
      {/* Hero Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-6xl font-bold mb-4 text-charcoal">Contact Us</h1>
          <div className="w-32 h-1 bg-olive mx-auto mb-6"></div>
          <p style={{ fontSize: '20px', color: '#4a7043', fontStyle: 'italic' }}>Contattaci</p>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mt-8">
            Located in the heart of Peterborough's historic Cowgate, we're here to serve you 
            authentic Italian cuisine. Get in touch for reservations, questions, or just to say hello!
          </p>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* 3-Column Grid for Contact Info */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            
            {/* Contact Details */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-6 bg-olive text-white">
                <h2 className="text-3xl font-bold text-white mb-2">Get In Touch</h2>
              </div>
              <div className="p-8">
                <div className="space-y-6">
                  {/* Address */}
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-charcoal mb-1">Address</h3>
                      <p className="text-gray-600">
                        The Pizza House<br />
                        3 Fortune Buildings, Cowgate<br />
                        Peterborough, Cambridgeshire<br />
                        PE1 1LR<br />
                        United Kingdom
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-olive rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-charcoal mb-1">Phone</h3>
                      <p className="text-gray-600">
                        <a href="tel:01733567929" className="hover:text-primary transition-colors">
                          01733 567929
                        </a>
                      </p>
                      <p className="text-sm text-gray-500 mt-1">Call ahead for large groups or special occasions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Opening Hours */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-6 bg-olive text-white">
                <h2 className="text-3xl font-bold text-white mb-2">Opening Hours</h2>
              </div>
              <div className="p-8">
              
              {/* Current Status */}
              {(() => {
                const status = getCurrentStatus();
                return (
                  <div className={`mb-6 p-4 rounded-lg ${
                    status.isOpen 
                      ? status.countdownType === 'closing-soon'
                        ? 'bg-yellow-100 border border-yellow-300'
                        : 'bg-green-100 border border-green-300'
                      : 'bg-red-100 border border-red-300'
                  }`}>
                    <div className="text-center">
                      <h3 className="font-bold text-lg text-charcoal mb-2">
                        {status.isOpen ? 'We are currently OPEN' : 'We are currently CLOSED'}
                      </h3>
                      <p className="text-gray-600 mb-2">Today's hours: {status.currentHours}</p>
                      <p className={`text-sm font-medium ${
                        status.isOpen 
                          ? status.countdownType === 'closing-soon'
                            ? 'text-yellow-700'
                            : 'text-green-700'
                          : 'text-red-700'
                      }`}>
                        {status.countdown}
                      </p>
                    </div>
                  </div>
                );
              })()}
              
              <div className="space-y-3">
                {openingHours.map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                    <span className="font-medium text-charcoal">{item.day}</span>
                    <span className="text-gray-600">{item.hours}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-olive bg-opacity-10 rounded-lg" style={{ background: '#4a7043', color: 'white' }}>
                <p className="text-sm font-medium" style={{ color: 'white' }}>
                  💡 Open every day of the week! Call ahead for large groups or special occasions.
                </p>
              </div>
            </div>
            </div>

            {/* Google Maps Section */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-6 bg-olive text-white">
                <h2 className="text-3xl font-bold text-white mb-2">Find Us</h2>
              </div>
              
              {/* Google Maps Embed */}
              <div className="relative">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2424.8574744864995!2d-0.24397132299999998!3d52.574334!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4877f14b2b6a2c6d%3A0x8e4b3c7a8a1b2c3d!2sThe%20Pizza%20House%2C%203%20Fortune%20Buildings%2C%20Cowgate%2C%20Peterborough%20PE1%201LR%2C%20UK!5e0!3m2!1sen!2suk!4v1703123456789!5m2!1sen!2suk"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Pizza House Location"
                  className="w-full"
                ></iframe>
              </div>
              
              <div className="p-6">
                <h3 className="font-bold text-lg text-charcoal mb-3">Getting Here</h3>
                <div className="space-y-2 text-gray-600 text-sm">
                  <p>🚶 <strong>On Foot:</strong> 5-minute walk from city centre</p>
                  <p>🚂 <strong>By Train:</strong> 10-minute walk from station</p>
                  <p>🚌 <strong>By Bus:</strong> 5-minute walk from Queensgate</p>
                  <p>🚗 <strong>By Car:</strong> Parking on Cowgate or Queensgate</p>
                </div>
                
                <div className="mt-4">
                  <a
                    href="https://maps.app.goo.gl/cKDet3KDbWArBQgQ8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-primary text-white px-4 py-2 rounded-lg font-bold hover:bg-red-700 transition-colors shadow-md hover:shadow-lg text-sm"
                  >
                    Open in Google Maps
                  </a>
                </div>
              </div>
            </div>
          </div>


        </div>
      </section>

      {/* Footer */}
      <footer className="bg-charcoal text-white py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          {/* Food Hygiene Image */}
          <div className="mb-6">
            <img 
              src="/PizzaHouse-Food-Hygine.png" 
              alt="Food Hygiene Rating" 
              className="mx-auto h-16"
            />
          </div>
          
          {/* Address */}
          <p className="text-lg mb-4">The Pizza House, 3 Fortune Buildings, Cowgate, Peterborough, Cambridgeshire, PE1 1LR</p>
          
          {/* Copyright */}
          <p className="text-sm opacity-80">
            Copyright © {new Date().getFullYear()} The Pizza House Peterborough
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
